import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bell,
  ChefHat,
  Clock,
  Loader2,
  Minus,
  Plus,
  Search,
  ShoppingBag,
  Trash2,
  UtensilsCrossed,
  UserCog,
  X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { aed } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useWaiterLive } from "@/hooks/useWaiterLive";
import {
  KOT_STATUS_FLOW,
  KOT_STATUS_META,
  billHasKitchenSession,
  categorizeWaiterBillLines,
  kotSummaryFromBill,
  waiterHasPendingKot,
} from "@/lib/waiterBill";
import { diningPhaseMeta, formatSessionPreview } from "@/lib/tableSession";
import { billHasAssignedTable } from "@/lib/posTable";
import { tableWaiterName } from "@/lib/tableFloorUi";
import { billLineActionId, isStandaloneBillLine } from "@/lib/billLines";
import { itemHasSizes, itemPosPriceLabel } from "@/lib/menuItemSizes";
import { displayItemName } from "@/lib/menuLocale";
import PosSizePickerPopup from "@/components/pos/PosSizePickerPopup";
import { useLocale } from "@/contexts/LocaleProvider";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";

const STATUS = {
  Vacant: { bg: "bg-emerald-500", label: "Open", ring: "ring-emerald-300" },
  Reserved: { bg: "bg-violet-600", label: "Reserved", ring: "ring-violet-300" },
  Occupied: { bg: "bg-rose-500", label: "Dining", ring: "ring-rose-300" },
  Printed: { bg: "bg-amber-400", label: "Printed", ring: "ring-amber-300" },
  Multi: { bg: "bg-indigo-500", label: "Multi", ring: "ring-indigo-300" },
};

/** Keep toasts off the fixed bottom cart bar on mobile. */
const WAITER_TOAST = { position: "top-center", duration: 2200 };

function waiterToastSuccess(message, options = {}) {
  toast.success(message, { ...WAITER_TOAST, ...options });
}

function waiterToastError(message) {
  toast.error(message, { ...WAITER_TOAST, duration: 3500 });
}

function waiterToastInfo(message) {
  toast.info(message, WAITER_TOAST);
}

function pushWaiterNotification(title, body, tag) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  try {
    new Notification(title, { body, tag });
  } catch {
    /* ignore */
  }
}

function WaiterKitchenSection({ status, lines }) {
  if (!lines.length) return null;
  const meta = KOT_STATUS_META[status];

  return (
    <div className={cn("rounded-2xl border p-3", meta.section, status === "Ready" && "ring-2 ring-emerald-400")}>
      <div className="flex items-center justify-between mb-2">
        <span className={cn("text-[11px] font-bold uppercase tracking-wide", meta.pulse && "text-emerald-800")}>
          {meta.label}
        </span>
        <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full border", meta.badge)}>
          {lines.reduce((s, l) => s + Number(l.qty || 0), 0)}
        </span>
      </div>
      <div className="space-y-2">
        {lines.map((line) => (
          <div key={`${status}-${line.id}`} className="flex items-center justify-between gap-2">
            <span className="text-[14px] font-semibold text-slate-900 truncate">{displayItemName(line)}</span>
            <span className="text-[13px] font-mono font-bold text-slate-700 shrink-0">×{line.qty}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Waiter() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuth();
  const branchId = user?.default_branch?.id;
  const counterId = user?.default_counter?.id;

  const [selectedTable, setSelectedTable] = useState(null);
  const [bill, setBill] = useState(null);
  const [category, setCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [cartPulse, setCartPulse] = useState(false);
  const [viewMode, setViewMode] = useState("order");
  const [sizePickItem, setSizePickItem] = useState(null);
  const kotNotifiedRef = useRef(new Set());

  const refetchBill = useCallback(async (billId) => {
    const id = billId ?? bill?.id;
    if (!id || !branchId) return;
    const { data } = await api.get(`/bills/${id}`, { params: { branch_id: branchId } });
    setBill(data.bill);
  }, [bill?.id, branchId]);

  const handleKotEvent = useCallback(
    ({ action, order }) => {
      if (!order || action !== "status_updated") return;

      const status = order.status;
      const tableLabel = order.table || order.bill_number || "Table";
      const items = (order.items || []).map((i) => `${i.qty}× ${i.name}`).join(", ");
      const eventKey = `${order.id}-${status}`;
      const matchesOpenBill =
        bill?.id && (order.sales_header_id === bill.id || order.bill_id === bill.id);
      const matchesOpenTable =
        selectedTable &&
        order.table &&
        (order.table === selectedTable.code || order.table === selectedTable.name);

      if (status === "Ready" && !kotNotifiedRef.current.has(eventKey)) {
        kotNotifiedRef.current.add(eventKey);
        waiterToastSuccess(`${tableLabel}: ready — ${items || order.kot_number}`, { duration: 6000 });
        pushWaiterNotification("Ready to serve", `${tableLabel}: ${items}`, eventKey);
        if (matchesOpenBill || matchesOpenTable) setViewMode("session");
      }

      if (status === "Preparing" && !kotNotifiedRef.current.has(eventKey)) {
        kotNotifiedRef.current.add(eventKey);
        if (matchesOpenBill || matchesOpenTable) {
          waiterToastInfo(`Kitchen preparing ${tableLabel}…`);
        }
      }
    },
    [bill?.id, selectedTable],
  );

  const liveStatus = useWaiterLive(branchId, {
    billId: bill?.id,
    onBillRefresh: () => refetchBill(),
    onKotEvent: handleKotEvent,
  });
  const isLive = liveStatus === "live";

  const { data: floorData, isLoading: tablesLoading, refetch: refetchFloor } = useQuery({
    queryKey: ["waiter-floor", branchId],
    queryFn: async () => {
      const { data } = await api.get("/tables/floor", { params: { branch_id: branchId } });
      return data;
    },
    enabled: Boolean(branchId),
    refetchInterval: isLive ? false : 15000,
  });

  useEffect(() => {
    if (typeof Notification === "undefined" || Notification.permission !== "default") return;
    Notification.requestPermission().catch(() => {});
  }, []);

  useEffect(() => {
    if (!bill?.id || !branchId || isLive) return undefined;
    const timer = window.setInterval(() => refetchBill(), 15000);
    return () => window.clearInterval(timer);
  }, [bill?.id, branchId, refetchBill, isLive]);

  useEffect(() => {
    if (!bill?.id || !floorData?.tables) return;
    const row = floorData.tables.find((t) => t.id === selectedTable?.id);
    if (row) setSelectedTable(row);
  }, [bill?.id, floorData, selectedTable?.id]);

  const tables = floorData?.tables ?? [];
  const areas = useMemo(() => Array.from(new Set(tables.map((t) => t.area).filter(Boolean))), [tables]);

  useEffect(() => {
    if (!areas.length) return;
    if (!category || !areas.includes(category)) {
      setCategory(areas[0]);
    }
  }, [areas, category]);

  const filteredTables = useMemo(
    () => (category ? tables.filter((t) => t.area === category) : tables),
    [tables, category],
  );

  const { data: menuItems = [], isLoading: menuLoading } = useQuery({
    queryKey: ["waiter-menu", branchId],
    queryFn: async () => {
      const { data } = await api.get("/menu-items", {
        params: { branch_id: branchId, show_in_pos: 1 },
      });
      return (data.menu_items || []).filter((m) => m.show_in_pos !== false && m.status === "Active");
    },
    enabled: Boolean(branchId) && Boolean(bill),
  });

  const menuCategories = useMemo(
    () => Array.from(new Set(menuItems.map((m) => m.category || "Other"))),
    [menuItems],
  );

  const [menuCat, setMenuCat] = useState(null);

  useEffect(() => {
    if (!menuCategories.length) return;
    if (!menuCat || !menuCategories.includes(menuCat)) {
      setMenuCat(menuCategories[0]);
    }
  }, [menuCategories, menuCat]);

  const filteredMenu = useMemo(() => {
    const q = search.trim().toLowerCase();
    return menuItems.filter((m) => {
      if (menuCat && (m.category || "Other") !== menuCat) return false;
      if (!q) return true;
      return m.name?.toLowerCase().includes(q) || m.name_ar?.includes(search.trim()) || m.code?.toLowerCase().includes(q);
    });
  }, [menuItems, menuCat, search]);

  const openTableMutation = useMutation({
    mutationFn: async (table) => {
      if (table.active_bills?.length) {
        const billId = table.active_bills[0].id;
        const { data } = await api.get(`/bills/${billId}`, { params: { branch_id: branchId } });
        return { table, bill: data.bill };
      }
      if (!counterId) throw new Error("No counter assigned");
      const { data } = await api.post(
        `/tables/${table.id}/orders`,
        {},
        { params: { branch_id: branchId, counter_id: counterId } },
      );
      return { table, bill: data.bill };
    },
    onSuccess: ({ table, bill: opened }) => {
      setSelectedTable(table);
      setBill(opened);
      setCartOpen(false);
      const hasKitchen = billHasKitchenSession(opened.lines, kotSummaryFromBill(opened));
      setViewMode(hasKitchen ? "session" : "order");
      waiterToastSuccess(hasKitchen ? `Table ${table.name} — order in progress` : `Table ${table.name} ready`);
    },
    onError: (err) => {
      waiterToastError(err.response?.data?.message || err.message || "Could not open table");
    },
  });

  const addLineMutation = useMutation({
    mutationFn: async ({ item, size }) => {
      const { data } = await api.post(
        `/bills/${bill.id}/lines`,
        { menu_item_id: item.id, qty: 1, ...(size ? { size } : {}) },
        { params: { branch_id: branchId } },
      );
      return data.bill;
    },
    onSuccess: (updated) => {
      setSizePickItem(null);
      setBill(updated);
      setCartPulse(true);
      window.setTimeout(() => setCartPulse(false), 400);
    },
    onError: (err) => {
      const tableErr = err.response?.data?.errors?.table?.[0];
      waiterToastError(
        tableErr || err.response?.data?.message || "Failed to add item",
      );
    },
  });

  function tapMenuItem(item) {
    if (itemHasSizes(item)) {
      setSizePickItem(item);
      return;
    }
    addLineMutation.mutate({ item });
  }

  const qtyMutation = useMutation({
    mutationFn: async ({ lineId, delta }) => {
      const { data } = await api.patch(
        `/bills/${bill.id}/lines/${lineId}`,
        { qty_delta: delta },
        { params: { branch_id: branchId } },
      );
      return data.bill;
    },
    onSuccess: (updated) => setBill(updated),
    onError: (err) => waiterToastError(err.response?.data?.message || "Qty update failed"),
  });

  const voidLineMutation = useMutation({
    mutationFn: async (lineId) => {
      const { data } = await api.post(
        `/bills/${bill.id}/lines/${lineId}/void`,
        {},
        { params: { branch_id: branchId } },
      );
      return data.bill;
    },
    onSuccess: (updated) => setBill(updated),
    onError: (err) => waiterToastError(err.response?.data?.message || "Void failed"),
  });

  const closeSessionMutation = useMutation({
    mutationFn: async (tableId) => {
      const { data } = await api.post(
        `/tables/${tableId}/session/close`,
        {},
        { params: { branch_id: branchId } },
      );
      return data;
    },
  });

  const kotMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(
        `/bills/${bill.id}/kot/send`,
        {},
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: async (res) => {
      waiterToastSuccess(res.message || "Sent to kitchen");
      if (res.bill) {
        setBill(res.bill);
      } else {
        await refetchBill();
      }
      queryClient.invalidateQueries({ queryKey: ["waiter-floor", branchId] });
      setCartOpen(false);
      setViewMode("session");
    },
    onError: (err) => {
      const data = err.response?.data;
      const msg =
        data?.errors?.table?.[0] ||
        data?.errors?.bill?.[0] ||
        data?.message ||
        "KOT send failed";
      waiterToastError(msg);
    },
  });

  const billLines = bill?.lines ?? [];
  const kotSummary = kotSummaryFromBill(bill);
  const { pending: pendingLines, kitchen, hasKitchenActivity } = useMemo(
    () => categorizeWaiterBillLines(billLines),
    [billLines],
  );
  const kitchenLines = useMemo(
    () => KOT_STATUS_FLOW.flatMap((status) => kitchen[status] || []),
    [kitchen],
  );
  const lineCount = pendingLines.reduce((s, l) => s + Number(l.qty || 0), 0);
  const pendingTotal = pendingLines.reduce((s, l) => s + Number(l.amount || 0), 0);
  const hasPendingKot = waiterHasPendingKot(billLines);
  const showSessionView = viewMode === "session" && hasKitchenActivity;
  const busy = openTableMutation.isPending || addLineMutation.isPending || kotMutation.isPending;

  function handleSendKot() {
    if (!billHasAssignedTable(bill)) {
      waiterToastError(t("waiter.tableRequiredForKot", "Select a table before sending to kitchen"));
      backToTables();
      return;
    }
    kotMutation.mutate();
  }

  async function backToTables() {
    const tableId = selectedTable?.id;
    const shouldClear = bill && (bill.lines?.length ?? 0) === 0 && tableId;

    if (shouldClear) {
      try {
        await closeSessionMutation.mutateAsync(tableId);
      } catch {
        /* keep session if clear fails (e.g. items added elsewhere) */
      }
    }

    setSelectedTable(null);
    setBill(null);
    setSearch("");
    setCartOpen(false);
    setViewMode("order");
    refetchFloor();
    queryClient.invalidateQueries({ queryKey: ["waiter-floor", branchId] });
  }

  if (!branchId) {
    return (
      <div className="h-full grid place-items-center text-slate-500 text-sm p-6 text-center">
        Select a branch to use waiter ordering.
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-slate-50 to-slate-100" dir={isRtl ? "rtl" : "ltr"} data-testid="waiter-screen">
      <header className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shadow-sm">
        {bill ? (
          <button
            type="button"
            onClick={backToTables}
            className="h-10 w-10 rounded-xl bg-slate-100 grid place-items-center text-slate-700"
            aria-label={t("waiter.backToTables")}
          >
            <ArrowLeft size={20} />
          </button>
        ) : (
          <div className="h-10 w-10 rounded-xl bg-emerald-600 grid place-items-center text-white">
            <UtensilsCrossed size={20} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">{t("waiter.title")}</div>
          <div className="text-[16px] font-bold text-slate-900 truncate">
            {bill ? `${t("waiter.table")} ${selectedTable?.name}` : user?.name}
          </div>
          {bill && (
            <div className="text-[11px] text-slate-500 font-medium truncate">
              {kotSummary?.ready_qty > 0
                ? t("waiter.readyToServeCount", { count: kotSummary.ready_qty })
                : selectedTable?.dining_phase_label || t("waiter.takingOrder")}
              {selectedTable?.elapsed ? ` · ${selectedTable.elapsed}` : ""}
              {liveStatus === "live" && <span className="text-emerald-600"> · {t("waiter.live")}</span>}
            </div>
          )}
        </div>
        <LanguageSwitcher />
        <button
          type="button"
          onClick={async () => {
            await logout();
            navigate("/login", { replace: true });
          }}
          className="text-[12px] font-semibold text-slate-500 px-3 py-2 rounded-lg hover:bg-slate-100"
        >
          {t("waiter.signOut")}
        </button>
      </header>

      {!bill ? (
        <>
          <div className="flex-shrink-0 px-4 py-3 flex gap-2 overflow-x-auto">
            {areas.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setCategory(a)}
                className={cn(
                  "shrink-0 px-4 py-2 rounded-full text-[13px] font-semibold border transition-colors",
                  category === a
                    ? "bg-emerald-600 border-emerald-600 text-white"
                    : "bg-white border-slate-200 text-slate-600",
                )}
              >
                {a}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-auto px-4 pb-4">
            {tablesLoading ? (
              <div className="h-40 grid place-items-center text-slate-400">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {filteredTables.map((table) => {
                  const s = STATUS[table.status] || STATUS.Vacant;
                  const phase = diningPhaseMeta(table.dining_phase);
                  const preview = formatSessionPreview(table.session);
                  const waiterName = tableWaiterName(table);
                  return (
                    <button
                      key={table.id}
                      type="button"
                      data-testid={`waiter-table-${table.code}`}
                      disabled={busy}
                      onClick={() => openTableMutation.mutate(table)}
                      className={cn(
                        "rounded-2xl p-4 text-left text-white shadow-md transition-transform active:scale-[0.98] min-h-[120px] flex flex-col",
                        s.bg,
                        busy && "opacity-60",
                      )}
                    >
                      <div className="text-[22px] font-black leading-none">{table.name}</div>
                      <div className="text-[11px] font-semibold opacity-90 mt-1">{table.area}</div>
                      {table.status !== "Vacant" && waiterName && (
                        <div className="text-[10px] font-semibold opacity-90 mt-1 flex items-center gap-1 truncate">
                          <UserCog size={11} className="shrink-0" />
                          {waiterName}
                        </div>
                      )}
                      {table.status !== "Vacant" ? (
                        <>
                          <div className="mt-auto pt-2 text-[11px] font-bold uppercase tracking-wide opacity-95">
                            {table.dining_phase_label || phase.label}
                            {table.elapsed ? ` · ${table.elapsed}` : ""}
                          </div>
                          {preview && (
                            <div className="text-[10px] leading-snug mt-1 opacity-90 line-clamp-2">{preview}</div>
                          )}
                          {table.session?.ready_kot_qty > 0 && (
                            <div className="text-[10px] font-bold mt-1 bg-white/25 rounded-lg px-2 py-0.5 inline-flex items-center gap-1 animate-pulse">
                              <Bell size={10} />
                              {table.session.ready_kot_qty} {t("waiter.ready")}
                            </div>
                          )}
                          {(table.line_count > 0 || table.session?.in_kitchen_qty > 0) && (
                            <div className="text-[10px] font-mono mt-1 opacity-95">
                              {table.session?.preparing_kot_qty > 0 && t("waiter.preparingCount", { count: table.session.preparing_kot_qty })}
                              {table.session?.preparing_kot_qty > 0 && table.session?.in_kitchen_qty > 0 && " · "}
                              {table.session?.in_kitchen_qty > 0 && !table.session?.ready_kot_qty && t("waiter.inKitchenCount", { count: table.session.in_kitchen_qty })}
                              {table.net_amount > 0 && ` · ${aed(table.net_amount || table.total_net_amount || 0)}`}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="mt-auto pt-2 text-[11px] font-bold uppercase tracking-wide opacity-90">
                          {t("waiter.tapToSeat")}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="flex-shrink-0 px-4 py-2 border-b border-slate-200 bg-white flex gap-2">
            {hasKitchenActivity && (
              <button
                type="button"
                onClick={() => setViewMode("session")}
                className={cn(
                  "flex-1 h-10 rounded-xl text-[13px] font-bold transition-colors",
                  showSessionView ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600",
                )}
                data-testid="waiter-tab-session"
              >
                {t("waiter.tableOrder")}
              </button>
            )}
            <button
              type="button"
              onClick={() => setViewMode("order")}
              className={cn(
                "flex-1 h-10 rounded-xl text-[13px] font-bold transition-colors",
                !showSessionView ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600",
              )}
              data-testid="waiter-tab-add"
            >
              {hasKitchenActivity ? t("waiter.addItems") : t("waiter.menu")}
            </button>
          </div>

          {showSessionView ? (
            <div className="flex-1 overflow-auto p-4 pb-28 space-y-3" data-testid="waiter-session-view">
              <div className="flex flex-wrap gap-2">
                {kotSummary?.ready_qty > 0 && (
                  <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                    {kotSummary.ready_qty} {t("waiter.ready")}
                  </span>
                )}
                {kotSummary?.preparing_qty > 0 && (
                  <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-orange-100 text-orange-800 border border-orange-200">
                    {t("waiter.preparingCount", { count: kotSummary.preparing_qty })}
                  </span>
                )}
                {kotSummary?.new_qty > 0 && (
                  <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                    {t("waiter.withKitchen", { count: kotSummary.new_qty })}
                  </span>
                )}
                {selectedTable?.elapsed && (
                  <span className="text-[11px] font-mono px-3 py-1 rounded-full bg-slate-100 text-slate-600 flex items-center gap-1">
                    <Clock size={11} />
                    {selectedTable.elapsed}
                  </span>
                )}
              </div>

              <WaiterKitchenSection status="Ready" lines={kitchen.Ready} />
              <WaiterKitchenSection status="Preparing" lines={kitchen.Preparing} />
              <WaiterKitchenSection status="New" lines={kitchen.New} />
              <WaiterKitchenSection status="Served" lines={kitchen.Served} />

              {pendingLines.length > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-amber-800 mb-2">
                    {t("waiter.notSentYet", { count: lineCount })}
                  </div>
                  <div className="space-y-2">
                    {pendingLines.map((line) => (
                      <div key={line.id} className="flex justify-between text-[13px]">
                        <span className="font-semibold truncate">{displayItemName(line)}</span>
                        <span className="font-mono font-bold shrink-0 ml-2">×{line.qty}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCartOpen(true)}
                    className="mt-3 w-full h-10 rounded-xl bg-amber-600 text-white text-[13px] font-bold"
                  >
                    {t("waiter.reviewSend")}
                  </button>
                </div>
              )}

              {!hasKitchenActivity && pendingLines.length === 0 && (
                <p className="text-center text-slate-400 text-sm py-8">{t("waiter.noItems")}</p>
              )}
            </div>
          ) : (
            <>
              <div className="flex-shrink-0 px-4 py-2 border-b border-slate-200 bg-white">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("waiter.searchMenu")}
                    className="h-10 pl-9 bg-slate-50"
                    data-testid="waiter-menu-search"
                  />
                </div>
                <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                  {menuCategories.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setMenuCat(c)}
                      className={cn(
                        "shrink-0 px-3 py-1.5 rounded-lg text-[12px] font-semibold",
                        menuCat === c ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600",
                      )}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-auto p-4 pb-28">
                {menuLoading ? (
                  <div className="h-32 grid place-items-center">
                    <Loader2 className="h-7 w-7 animate-spin text-emerald-600" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {filteredMenu.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        disabled={addLineMutation.isPending}
                        onClick={() => tapMenuItem(item)}
                        className="rounded-2xl bg-white border border-slate-200 p-3 text-left shadow-sm hover:border-emerald-400 hover:shadow-md transition-all active:scale-[0.98] min-h-[96px] flex flex-col"
                        data-testid={`waiter-item-${item.code}`}
                      >
                        <div className="text-[14px] font-bold text-slate-900 leading-tight line-clamp-2">{displayItemName(item)}</div>
                        <div className="text-[11px] text-slate-500 mt-1">
                          {item.category}
                          {itemHasSizes(item) ? " · S / M / L" : ""}
                        </div>
                        <div className="mt-auto pt-2 text-[15px] font-black text-emerald-700 font-mono">
                          {itemHasSizes(item) ? `${itemPosPriceLabel(item)}` : aed(item.price)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex-shrink-0 fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
            <div className="px-4 py-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCartOpen(true)}
                className={cn(
                  "flex-1 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-between px-4 transition-transform",
                  cartPulse && "scale-[1.02] ring-2 ring-emerald-400",
                )}
                data-testid="waiter-cart-toggle"
              >
                <span className="flex items-center gap-2 text-[13px] font-semibold">
                  <ShoppingBag size={18} />
                  {lineCount} {t("waiter.items")}
                </span>
                <span className="font-mono font-bold">{aed(pendingTotal)}</span>
              </button>
              <Button
                type="button"
                disabled={!hasPendingKot || kotMutation.isPending}
                onClick={handleSendKot}
                className="h-12 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 font-bold"
                data-testid="waiter-send-kot"
              >
                {kotMutation.isPending ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <>
                    <ChefHat size={18} className="mr-1.5" />
                    {t("waiter.sendKot")}
                  </>
                )}
              </Button>
            </div>
          </div>

          {cartOpen && (
            <div className="fixed inset-0 z-40 flex flex-col justify-end bg-black/40" onClick={() => setCartOpen(false)}>
              <div
                className="bg-white rounded-t-3xl max-h-[70vh] flex flex-col shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <span className="font-bold text-slate-900">Order — {selectedTable?.name}</span>
                  <button type="button" onClick={() => setCartOpen(false)} className="p-2">
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 overflow-auto">
                  {pendingLines.length === 0 && kitchenLines.length === 0 ? (
                    <p className="p-6 text-center text-slate-400 text-sm">No items yet — tap menu to add</p>
                  ) : (
                    <>
                      {pendingLines.length > 0 && (
                        <div className="divide-y">
                          {pendingLines.map((line) => {
                            const isComboChild = !isStandaloneBillLine(line);
                            const actionLineId = billLineActionId(line);
                            return (
                              <div
                                key={line.id}
                                className={cn(
                                  "px-4 py-3 flex items-center gap-3",
                                  isComboChild && "pl-8 bg-slate-50/80",
                                  line.is_combo_header && "bg-violet-50/50",
                                )}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-slate-900 truncate">{displayItemName(line)}</div>
                                  {isStandaloneBillLine(line) && (
                                    <div className="text-[12px] text-slate-500 font-mono">{aed(line.rate)} each</div>
                                  )}
                                  {isComboChild && (
                                    <div className="text-[11px] text-slate-500">{t("waiter.includedInCombo")}</div>
                                  )}
                                </div>
                                {isStandaloneBillLine(line) ? (
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      className="h-9 w-9 rounded-lg bg-slate-100 grid place-items-center"
                                      onClick={() => {
                                        if (line.qty <= 1) voidLineMutation.mutate(actionLineId);
                                        else qtyMutation.mutate({ lineId: actionLineId, delta: -1 });
                                      }}
                                    >
                                      {line.qty <= 1 ? <Trash2 size={14} /> : <Minus size={14} />}
                                    </button>
                                    <span className="w-8 text-center font-mono font-bold">{line.qty}</span>
                                    <button
                                      type="button"
                                      className="h-9 w-9 rounded-lg bg-emerald-100 text-emerald-800 grid place-items-center"
                                      onClick={() => qtyMutation.mutate({ lineId: actionLineId, delta: 1 })}
                                    >
                                      <Plus size={14} />
                                    </button>
                                  </div>
                                ) : (
                                  <span className="w-8 text-center font-mono font-bold text-slate-600">{line.qty}</span>
                                )}
                                <div className="w-16 text-right font-mono font-bold text-sm">
                                  {isStandaloneBillLine(line) ? aed(line.amount) : "—"}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {kitchenLines.length > 0 && (
                        <div className="border-t bg-slate-50">
                          <div className="px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                            {t("waiter.kitchenStatus")}
                          </div>
                          <div className="divide-y divide-slate-200">
                            {kitchenLines.map((line) => {
                              const status = line.kot_kitchen_status || "New";
                              const meta = KOT_STATUS_META[status] || KOT_STATUS_META.New;
                              return (
                                <div key={`sent-${status}-${line.id}`} className="px-4 py-2.5 flex items-center gap-2">
                                  <span className={cn("text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border shrink-0", meta.badge)}>
                                    {meta.short}
                                  </span>
                                  <span className="truncate text-[13px] text-slate-700 flex-1">{displayItemName(line)}</span>
                                  <span className="text-[12px] font-mono shrink-0">×{line.qty}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {pendingLines.length === 0 && kitchenLines.length > 0 && (
                        <p className="px-4 py-3 text-center text-[12px] text-emerald-700 bg-emerald-50">
                          {t("waiter.allSentAddMore")}
                        </p>
                      )}
                    </>
                  )}
                </div>
                <div className="px-4 py-3 border-t flex justify-between font-bold">
                  <span>{pendingLines.length ? t("waiter.newItems") : t("waiter.billTotal")}</span>
                  <span className="font-mono text-emerald-700">
                    {aed(pendingLines.length ? pendingTotal : bill?.net_amount || 0)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <PosSizePickerPopup
        open={Boolean(sizePickItem)}
        item={sizePickItem}
        pending={addLineMutation.isPending}
        onPick={(size) => addLineMutation.mutate({ item: sizePickItem, size })}
        onClose={() => setSizePickItem(null)}
      />
    </div>
  );
}
